import { Alert, Button, Form, FormInstance, Input, Radio, Select } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import CollapsibleCard from 'components/CollapsibleCard';
import InfoCollapse from 'components/InfoCollapse';
import InlineFileUpload from 'components/InlineFileUpload';
import PrecariteInfo from 'components/PrecariteInfo';
import { MAX_UPLOAD_FILESIZE } from 'const/attachments';
import { emailRule, phoneRule, requiredRule } from 'const/formRules';
import { useAuthContext } from 'contexts/AuthContext';
import type { EventArgs } from 'rc-field-form/lib/interface';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClientType,
  NouveauDossierConcessionnaireData,
  statutPrecarite,
  TypeStructure,
  VehiculeType,
} from 'types/Concessionnaire';
import { getValueFromEvent } from 'utils/files';
import AddressFormFields, {
  AddressFormFieldsConfig,
} from 'views/dossiers/concessionnaire/steps/components/AddressFormFields';
import SiretAndRaisonSociale from 'views/dossiers/concessionnaire/steps/components/SiretAndRaisonSociale';
import useStep1PersonneMoraleFlow from 'views/dossiers/concessionnaire/steps/step1/useStep1PersonneMoraleFlow';

const STATUT_PRECARITE_OPTIONS = [
  statutPrecarite.PRECAIRE,
  statutPrecarite.MODESTE,
  statutPrecarite.CLASSIQUE,
] as const;

const ETABLISSEMENT_SECTION_FIELDS: (keyof NouveauDossierConcessionnaireData)[] =
  [
    'siret',
    'raisonSociale',
    'etablissement',
    'adresse',
    'codePostal',
    'ville',
    'typeStructure',
  ];

const ETABLISSEMENT_SECTION_REQUIRED_FIELDS: (keyof NouveauDossierConcessionnaireData)[] =
  ['siret', 'raisonSociale', 'etablissement', 'adresse', 'codePostal', 'ville'];

const BASE_COORDONNEES_SECTION_FIELDS: (keyof NouveauDossierConcessionnaireData)[] =
  ['lastname', 'firstname', 'phone', 'email', 'fonction', 'typeAcheteur'];

interface Props {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  restoredAvisImpositionFiles?: UploadFile[];
  onAvisImpositionChange?: (
    files?: NouveauDossierConcessionnaireData['avisImposition'],
  ) => void;
  onSubmitAvailabilityChange?: (isSubmittable: boolean) => void;
}

function isFilledValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function areFieldsFilled(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  return fields.every((field) => isFilledValue(form.getFieldValue(field)));
}

function areFieldsValid(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  fields: (keyof NouveauDossierConcessionnaireData)[],
) {
  const hasErrors = form
    .getFieldsError(fields)
    .some((fieldState) => fieldState.errors.length > 0);
  const hasValidating = fields.some((field) => form.isFieldValidating(field));

  return !hasErrors && !hasValidating;
}

function isSectionSubmittable(
  form: FormInstance<NouveauDossierConcessionnaireData>,
  requiredFields: (keyof NouveauDossierConcessionnaireData)[],
  validationFields: (keyof NouveauDossierConcessionnaireData)[] = requiredFields,
) {
  return (
    areFieldsFilled(form, requiredFields) &&
    areFieldsValid(form, validationFields)
  );
}

export default function Step1({
  form,
  restoredAvisImpositionFiles,
  onAvisImpositionChange,
  onSubmitAvailabilityChange,
}: Props) {
  const { t } = useTranslation();
  const { auth } = useAuthContext();

  const companyAddressConfig: AddressFormFieldsConfig = useMemo(
    () => ({
      fieldNames: {
        address: 'adresse',
        postalCode: 'codePostal',
        city: 'ville',
      },
      labels: {
        address: t('nouveau_formulaire:adresse:label'),
        postalCode: t('nouveau_formulaire:code_postal:label'),
        city: t('nouveau_formulaire:ville:label'),
      },
      placeholders: {
        address: t('nouveau_formulaire:adresse:placeholder'),
        postalCode: t('nouveau_formulaire:code_postal:placeholder'),
        city: t('nouveau_formulaire:ville:placeholder'),
      },
      queryKey: 'company-addresses',
    }),
    [t],
  );

  const personnePhysiqueAddressConfig: AddressFormFieldsConfig = useMemo(
    () => ({
      fieldNames: {
        address: 'adresse',
        postalCode: 'codePostal',
        city: 'ville',
      },
      labels: {
        address: t('nouveau_formulaire:adresse:label_short'),
        postalCode: t('nouveau_formulaire:code_postal:label_short'),
        city: t('nouveau_formulaire:ville:label_short'),
      },
      placeholders: {
        address: t('nouveau_formulaire:adresse:placeholder'),
        postalCode: t('nouveau_formulaire:code_postal:placeholder'),
        city: t('nouveau_formulaire:ville:placeholder'),
      },
      queryKey: 'personne-physique-addresses',
    }),
    [t],
  );

  const typeClient = Form.useWatch('typeClientFinal', form);
  const vehiculeType = Form.useWatch('vehiculeType', form);
  const typeAcheteur = Form.useWatch('typeAcheteur', form);
  const personnePhysique = typeClient === ClientType.PERSONNE_PHYSIQUE;
  const isVehiculeLourd = vehiculeType !== VehiculeType.LEGER;

  const shouldDisplayMainFields =
    !!typeClient && (personnePhysique || !!vehiculeType);
  const shouldShowVehiculeDemonstration =
    !personnePhysique && typeAcheteur === 'loueur_ou_vendeur';

  const coordonneesSectionFields = useMemo<
    (keyof NouveauDossierConcessionnaireData)[]
  >(
    () =>
      shouldShowVehiculeDemonstration
        ? [
            ...BASE_COORDONNEES_SECTION_FIELDS,
            'isVehiculeDemonstration' as keyof NouveauDossierConcessionnaireData,
          ]
        : BASE_COORDONNEES_SECTION_FIELDS,
    [shouldShowVehiculeDemonstration],
  );

  const flow = useStep1PersonneMoraleFlow({
    form,
    personnePhysique,
    etablissementSectionFields: ETABLISSEMENT_SECTION_FIELDS,
    coordonneesSectionFields,
  });

  const isStep1Submittable = personnePhysique
    ? true
    : flow.isEtablissementSectionSaved && flow.isCoordonneesSectionSaved;

  const fichesDeLiaison = useMemo(
    () => auth?.user?.fichesDeLiaison || [],
    [auth?.user?.fichesDeLiaison],
  );
  const showMarqueField = fichesDeLiaison.length > 1;
  const restoredAvisImpositionAppliedRef = useRef(false);

  const handleAvisImpositionFromEvent = useCallback(
    (event: EventArgs[0]) => {
      const files = getValueFromEvent(event) as
        | NouveauDossierConcessionnaireData['avisImposition']
        | undefined;
      onAvisImpositionChange?.(files);
      return files;
    },
    [onAvisImpositionChange],
  );

  useEffect(() => {
    onSubmitAvailabilityChange?.(isStep1Submittable);
  }, [isStep1Submittable, onSubmitAvailabilityChange]);

  // Automatically set ficheDeLiaison when there's only one option or user is personne physique.
  useEffect(() => {
    if (!personnePhysique && fichesDeLiaison.length === 1) {
      form.setFieldValue('ficheDeLiaison', fichesDeLiaison[0]);
      return;
    }

    if (personnePhysique) {
      form.setFieldValue('ficheDeLiaison', undefined);
    }
  }, [fichesDeLiaison, form, personnePhysique]);

  useEffect(() => {
    if (!personnePhysique || restoredAvisImpositionAppliedRef.current) {
      return;
    }
    if (!restoredAvisImpositionFiles?.length) {
      return;
    }

    const currentFiles = form.getFieldValue('avisImposition') as
      | UploadFile[]
      | undefined;
    if (!currentFiles?.length) {
      form.setFieldValue('avisImposition', restoredAvisImpositionFiles);
    }
    restoredAvisImpositionAppliedRef.current = true;
  }, [form, personnePhysique, restoredAvisImpositionFiles]);

  useEffect(() => {
    if (!restoredAvisImpositionFiles?.length) {
      restoredAvisImpositionAppliedRef.current = false;
    }
  }, [restoredAvisImpositionFiles]);

  const renderContactFields = (includeFonction: boolean) => (
    <>
      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:lastname:label')}
        name="lastname"
        required
        rules={[requiredRule]}
      >
        <Input placeholder={t('nouveau_formulaire:lastname:placeholder')} />
      </Form.Item>

      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:firstname:label')}
        name="firstname"
        required
        rules={[requiredRule]}
      >
        <Input placeholder={t('nouveau_formulaire:firstname:placeholder')} />
      </Form.Item>

      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:phone:label')}
        name="phone"
        required
        rules={[requiredRule, phoneRule]}
      >
        <Input placeholder={t('nouveau_formulaire:phone:placeholder')} />
      </Form.Item>

      <Form.Item<NouveauDossierConcessionnaireData>
        label={t('nouveau_formulaire:email:label')}
        name="email"
        required
        rules={[requiredRule, emailRule]}
      >
        <Input placeholder={t('nouveau_formulaire:email:placeholder')} />
      </Form.Item>

      {includeFonction && (
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:fonction:label')}
          name="fonction"
          required
          rules={[requiredRule]}
        >
          <Input
            maxLength={128}
            placeholder={t('nouveau_formulaire:fonction:placeholder')}
          />
        </Form.Item>
      )}
    </>
  );

  const renderTypeAcheteurField = () => {
    const options = isVehiculeLourd
      ? ['agglo_less_250', 'agglo_more_250', 'other']
      : [
          'company_less_100',
          'company_more_100',
          'org_less_20',
          'org_more_20',
          'loueur_ou_vendeur',
        ];

    return (
      <Form.Item<NouveauDossierConcessionnaireData>
        required
        label={t('nouveau_formulaire:typeAcheteur:label')}
      >
        <Form.Item<NouveauDossierConcessionnaireData>
          name="typeAcheteur"
          rules={[requiredRule]}
          noStyle
        >
          <Select
            placeholder={t('nouveau_formulaire:typeAcheteur:placeholder')}
            className="mb-2"
          >
            {options.map((value) => (
              <Select.Option key={value} value={value}>
                {t(`nouveau_formulaire:typeAcheteur:options:${value}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <InfoCollapse
          i18nKey="nouveau_formulaire:nb_vehicules:info"
          className="!bg-bleu-hellio-1 [&_.ant-collapse-content-box]:!bg-bleu-hellio-1 [&_.ant-collapse-content-active]:-mt-4"
        />
      </Form.Item>
    );
  };

  const renderPersonnePhysiqueFields = () => (
    <>
      {renderContactFields(false)}

      <AddressFormFields config={personnePhysiqueAddressConfig} />

      <Form.Item<NouveauDossierConcessionnaireData>
        required
        label={t('nouveau_formulaire:status_precarite:label')}
      >
        <div className="mb-4">
          <div className="text-gray-500">
            {t('nouveau_formulaire:status_precarite:hint')}
          </div>
          <Form.Item<NouveauDossierConcessionnaireData>
            name="statutPrecarite"
            rules={[requiredRule]}
            noStyle
          >
            <Radio.Group className="[&_span]:font-normal">
              {STATUT_PRECARITE_OPTIONS.map((status) => (
                <Radio key={status} value={status}>
                  {status}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </div>

        <InfoCollapse
          label={t('nouveau_formulaire:status_precarite:info_label')}
        >
          <PrecariteInfo />
        </InfoCollapse>
      </Form.Item>

      <Form.Item<NouveauDossierConcessionnaireData>
        name="avisImposition"
        valuePropName="fileList"
        required
        rules={[requiredRule]}
        getValueFromEvent={handleAvisImpositionFromEvent}
        label={t('nouveau_formulaire:avis_imposition:label')}
      >
        <InlineFileUpload
          browseLabel={t('add_dossier_beneficiary:files_browse')}
          maxSizeLabel={t('add_dossier_beneficiary:files_max_size', {
            size: MAX_UPLOAD_FILESIZE,
          })}
          multiple={false}
          maxCount={1}
        />
      </Form.Item>
    </>
  );

  const renderPersonneMoraleFields = () => (
    <div className="space-y-4">
      {fichesDeLiaison.length === 0 && (
        <Alert
          message={t('primes_table:no_active_contract')}
          type="error"
          showIcon
          className="mb-6"
        />
      )}

      {fichesDeLiaison.length > 0 && (
        <Form.Item<NouveauDossierConcessionnaireData>
          name="ficheDeLiaison"
          label={t('nouveau_formulaire:marque_vehicule:label')}
          required
          rules={[requiredRule]}
          hidden={!showMarqueField}
          getValueFromEvent={(value) => {
            return fichesDeLiaison.find((fiche) => fiche.id === value);
          }}
          getValueProps={(value) => ({
            value: value?.id,
          })}
        >
          <Select
            placeholder={t('nouveau_formulaire:marque_vehicule:placeholder')}
          >
            {fichesDeLiaison.map((fiche) => (
              <Select.Option key={fiche.id} value={fiche.id}>
                {fiche.marque}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <CollapsibleCard
        title={t('nouveau_formulaire:etablissement_section_title')}
        isOpen={flow.isEtablissementSectionOpen}
        isComplete={flow.isEtablissementSectionSaved}
        onToggle={
          flow.isEtablissementSectionSaved
            ? flow.toggleEtablissementSection
            : undefined
        }
      >
        <div className="space-y-4">
          <SiretAndRaisonSociale form={form} />
          <AddressFormFields config={companyAddressConfig} />

          <Form.Item<NouveauDossierConcessionnaireData>
            name="typeStructure"
            label={t('nouveau_formulaire:typeStructure:label')}
          >
            <Select
              placeholder={t('nouveau_formulaire:typeStructure:placeholder')}
            >
              <Select.Option key="" value="">
                {t('nouveau_formulaire:typeStructure:emptyOption')}
              </Select.Option>
              {Object.values(TypeStructure).map((type) => (
                <Select.Option key={type} value={type}>
                  {t(`nouveau_formulaire:typeStructure:options:${type}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {() => (
              <Button
                type="primary"
                htmlType="button"
                onClick={flow.handleSaveEtablissementSection}
                shape="round"
                disabled={
                  !isSectionSubmittable(
                    form,
                    ETABLISSEMENT_SECTION_REQUIRED_FIELDS,
                    ETABLISSEMENT_SECTION_FIELDS,
                  )
                }
              >
                {t('global:enregistrer_et_continuer')}
              </Button>
            )}
          </Form.Item>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title={t('nouveau_formulaire:coordonnees')}
        isOpen={flow.isCoordonneesSectionOpen}
        showContent={flow.isEtablissementSectionSaved}
        isComplete={flow.isCoordonneesSectionSaved}
        onToggle={
          flow.isCoordonneesSectionSaved
            ? flow.toggleCoordonneesSection
            : undefined
        }
      >
        <div className="space-y-4">
          {renderContactFields(true)}
          {renderTypeAcheteurField()}

          {shouldShowVehiculeDemonstration && (
            <Form.Item<NouveauDossierConcessionnaireData>
              label={t('nouveau_formulaire:isVehiculeDemonstration:label')}
              name="isVehiculeDemonstration"
              required
              rules={[requiredRule]}
            >
              <Radio.Group className="[&_span]:font-normal">
                <Radio value={true}>
                  {t('nouveau_formulaire:isVehiculeDemonstration:options:oui')}
                </Radio>
                <Radio value={false}>
                  {t('nouveau_formulaire:isVehiculeDemonstration:options:non')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item noStyle shouldUpdate>
            {() => (
              <Button
                type="primary"
                htmlType="button"
                onClick={flow.handleSaveCoordonneesSection}
                shape="round"
                disabled={!isSectionSubmittable(form, coordonneesSectionFields)}
              >
                {t('global:enregistrer_et_continuer')}
              </Button>
            )}
          </Form.Item>
        </div>
      </CollapsibleCard>
    </div>
  );

  return (
    <Form
      form={form}
      name="step1"
      layout="vertical"
      className="[&_label]:font-bold [&_p]:font-bold"
      onValuesChange={flow.handleValuesChange}
    >
      <Form.Item<NouveauDossierConcessionnaireData>
        name="typeClientFinal"
        label={t('nouveau_formulaire:type_client:label')}
        rules={[{ required: true }]}
      >
        <Radio.Group
          className="[&_span]:font-normal"
          onChange={(event) => {
            if (event.target.value !== typeClient) {
              form.setFieldValue('vehiculeType', undefined);
            }
          }}
        >
          {Object.values(ClientType).map((value) => (
            <Radio value={value} key={value}>
              {value}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      {!!typeClient && !personnePhysique && (
        <Form.Item<NouveauDossierConcessionnaireData>
          name="vehiculeType"
          label={t('nouveau_formulaire:vehicule_type:label')}
          required
          rules={[requiredRule]}
        >
          <Select
            placeholder={t('nouveau_formulaire:vehicule_type:placeholder')}
          >
            {Object.values(VehiculeType).map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {shouldDisplayMainFields && (
        <>
          <Form.Item<NouveauDossierConcessionnaireData> name="accountId" hidden>
            <Input />
          </Form.Item>

          {personnePhysique
            ? renderPersonnePhysiqueFields()
            : renderPersonneMoraleFields()}
        </>
      )}
    </Form>
  );
}
